import { Avatar, Badge } from '../../../components';
import { Conversation } from '../../../types';
import { useConversationStore, useUIStore } from '../../../stores';
import { formatDate, truncate, cn } from '../../../lib/utils';
import { useIsDesktop } from '../../../lib/hooks/useMediaQuery';
import WhatsAppNumberBadge from './WhatsAppNumberBadge';

interface ConversationItemProps {
  conversation: Conversation;
  /**
   * True when the previous list item is for the same customer. Used to
   * visually cluster a customer's multiple sessions (different topics) under
   * one header rather than treating them as unrelated rows.
   */
  isContinuation?: boolean;
}

export default function ConversationItem({ conversation, isContinuation = false }: ConversationItemProps) {
  const { selectedConversationId, selectConversation } = useConversationStore();
  const { setMobileView } = useUIStore();
  const isDesktop = useIsDesktop();

  const isSelected = selectedConversationId === conversation.id;

  const getStatusBadge = () => {
    switch (conversation.state) {
      case 'bot_active':
        return <Badge variant="info" size="sm">Bot</Badge>;
      case 'staff_active':
        return <Badge variant="success" size="sm">Active</Badge>;
      case 'awaiting_staff':
        return <Badge variant="warning" size="sm">Waiting</Badge>;
      case 'resolved':
        return <Badge variant="default" size="sm">Resolved</Badge>;
    }
  };

  const getPriorityColor = () => {
    switch (conversation.priority) {
      case 'urgent':
        return 'border-l-danger';
      case 'high':
        return 'border-l-warning';
      default:
        return 'border-l-transparent';
    }
  };

  const getCategoryLabel = (): string | null => {
    if (!conversation.category || conversation.category === 'general') return null;
    const labels: Record<string, string> = {
      order_issue: 'Order',
      refund_request: 'Refund',
      shipping: 'Shipping',
      product_inquiry: 'Product',
      technical_support: 'Tech',
      billing: 'Billing',
      complaint: 'Complaint',
    };
    return labels[conversation.category] || null;
  };

  return (
    <button
      onClick={() => {
        selectConversation(conversation.id);
        if (!isDesktop) setMobileView('chat');
      }}
      className={cn(
        'w-full border-l-4 p-4 text-left transition-colors hover:bg-black/[0.04]',
        isSelected ? 'bg-brand/10' : 'bg-transparent',
        getPriorityColor()
      )}
    >
      <div className="flex items-start gap-3">
        {isContinuation ? (
          // Same customer as the row above — collapse the avatar into a thin
          // connector so the rows read as "another thread for Alice" instead
          // of "an unrelated row that happens to share a name".
          <div className="flex w-10 justify-center" aria-hidden>
            <div className="w-px bg-line-2" />
          </div>
        ) : (
          <Avatar
            name={conversation.customerName}
            src={conversation.customerAvatar}
            size="md"
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            {isContinuation ? (
              <span className="text-xs text-ink-3">Another conversation</span>
            ) : (
              <p className="font-medium text-ink truncate flex items-center gap-1">
                {conversation.channel === 'voice' ? (
                  <svg className="h-3.5 w-3.5 flex-shrink-0 text-purple-600" viewBox="0 0 20 20" fill="currentColor"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" /></svg>
                ) : conversation.channel === 'web' ? (
                  <svg className="h-3.5 w-3.5 flex-shrink-0 text-brand" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" /></svg>
                ) : (
                  <svg className="h-3.5 w-3.5 flex-shrink-0 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.118.549 4.107 1.513 5.838L0 24l6.326-1.455A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.97 0-3.834-.526-5.438-1.49l-.39-.232-3.756.864.9-3.632-.255-.406A9.698 9.698 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z" /></svg>
                )}
                {conversation.customerName}
              </p>
            )}
            <span className="text-xs text-ink-3 flex-shrink-0">
              {conversation.lastActivity ? formatDate(conversation.lastActivity) : ''}
            </span>
          </div>

          {/* Topic summary is the per-thread label; falls back to last
              message preview when the intelligence pipeline hasn't produced
              a summary yet (new sessions, or older sessions pre-feature). */}
          <p className="mt-0.5 text-sm text-ink-2 truncate">
            {conversation.topicSummary
              ? truncate(conversation.topicSummary, 80)
              : truncate(conversation.lastMessage || '', 60)}
          </p>

          <div className="mt-2 flex items-center gap-2">
            {getStatusBadge()}
            <WhatsAppNumberBadge conversation={conversation} />
            {conversation.lastCallStatus === 'missed' && (
              <Badge variant="danger" size="sm">
                <span className="flex items-center gap-1" title="Missed call">
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  Missed
                </span>
              </Badge>
            )}
            {conversation.sentiment === 'negative' && (
              <span className="inline-block h-2 w-2 rounded-full bg-danger" title="Negative sentiment" />
            )}
            {conversation.sentiment === 'positive' && (
              <span className="inline-block h-2 w-2 rounded-full bg-signal" title="Positive sentiment" />
            )}
            {getCategoryLabel() && (
              <span className="rounded-xs bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-ink-2">
                {getCategoryLabel()}
              </span>
            )}
            {conversation.unreadCount > 0 && (
              <Badge variant="danger" size="sm">
                {conversation.unreadCount}
              </Badge>
            )}
            {conversation.latestCsatScore != null && (
              <span
                className="inline-flex items-center gap-0.5 rounded-full border border-warning/50 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning"
                title={`Customer rated ${conversation.latestCsatScore}/5`}
              >
                <svg className="h-3 w-3 text-warning" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.366 2.446a1 1 0 00-.364 1.118l1.287 3.957c.3.922-.755 1.688-1.54 1.118l-3.366-2.446a1 1 0 00-1.176 0l-3.366 2.446c-.784.57-1.838-.196-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.07 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                </svg>
                {conversation.latestCsatScore}/5
              </span>
            )}
            {conversation.assignedStaffName && (
              <span className="text-xs text-ink-3">
                {conversation.assignedStaffName}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
